import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import styles from "../../styles.module.css";

/** TXM-325 item 01 — mesmos limiares do protótipo (apuracao-detalhes-filtros.js). */
const LIMITE_XLSX_PARTICAO = 1_000_000;
const LIMITE_XLSX_LINHA = 1_200_000;

/**
 * Modal de exportação de lançamentos (CSV / XLSX único / XLSX por ano).
 * Só se aplica ao export de "lançamentos" — "Exportar Agrupado" segue direto, sem esta modal.
 */
export default function ModalExportarLancamentos({
  open,
  onClose,
  totalLancamentos = 0,
  confirmando = false,
  onConfirm,
}) {
  const [formato, setFormato] = useState("csv");
  const [modoXlsx, setModoXlsx] = useState("unico");

  const excedeParticao = totalLancamentos > LIMITE_XLSX_PARTICAO;
  const excedeLinha = totalLancamentos > LIMITE_XLSX_LINHA;

  useEffect(() => {
    if (!open) return;
    setFormato("csv");
    setModoXlsx(excedeParticao ? "por-ano" : "unico");
  }, [open, excedeParticao]);

  const handleClose = () => {
    if (confirmando) return;
    onClose?.();
  };

  const bloqueadoPorLimite = formato === "xlsx" && modoXlsx === "unico" && excedeLinha;

  const handleConfirmar = () => {
    if (bloqueadoPorLimite) return;
    onConfirm?.({ formato, modoXlsx: formato === "xlsx" ? modoXlsx : "unico" });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <div className={styles.title}>Exportar lançamentos</div>
      </DialogTitle>
      <DialogContent className={styles.content}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Lançamentos no resultado do filtro: {totalLancamentos.toLocaleString("pt-BR")}
          {excedeParticao
            ? " (acima de 1 milhão — XLSX pode ser gerado em arquivos por ano)"
            : ""}
        </Typography>

        <FormControl component="fieldset" sx={{ mt: 1, width: "100%" }}>
          <FormLabel component="legend">Formato do arquivo</FormLabel>
          <RadioGroup
            row
            value={formato}
            onChange={(e) => setFormato(e.target.value)}
          >
            <FormControlLabel value="csv" control={<Radio />} label="CSV" />
            <FormControlLabel value="xlsx" control={<Radio />} label="Excel (XLSX)" />
          </RadioGroup>
        </FormControl>

        {formato === "xlsx" && (
          <FormControl
            component="fieldset"
            sx={{ mt: 1, pl: 2, width: "100%", borderLeft: "3px solid #e3eaf2" }}
          >
            <FormLabel component="legend">Organização do XLSX</FormLabel>
            <RadioGroup
              value={modoXlsx}
              onChange={(e) => setModoXlsx(e.target.value)}
            >
              <FormControlLabel value="unico" control={<Radio />} label="Arquivo único" />
              <FormControlLabel
                value="por-ano"
                control={<Radio />}
                label="Arquivos separados por ano"
              />
            </RadioGroup>

            {excedeParticao && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Acima de 1 milhão de lançamentos, use arquivos por ano para respeitar o
                limite de 1,2 milhão de linhas por arquivo Excel.
              </Alert>
            )}
            {modoXlsx === "unico" && excedeLinha && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Arquivo único excede 1,2 milhão de linhas. Escolha arquivos por ano ou
                exporte em CSV.
              </Alert>
            )}
          </FormControl>
        )}
      </DialogContent>

      <DialogActions className={styles.containerBtn}>
        <Button
          onClick={handleClose}
          variant="contained"
          className={styles.btnCancelar}
          disabled={confirmando}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmar}
          variant="contained"
          className={styles.btnSalvar}
          disabled={confirmando || bloqueadoPorLimite}
        >
          {confirmando ? "Exportando…" : "Exportar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
